import {
  AfterViewInit,
  Component, ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {AbstractControl, FormBuilder, FormGroup} from '@angular/forms';
import {concat, timer, Observable, Subscription} from 'rxjs';
import {mapTo, delay, repeatWhen} from 'rxjs/operators';

import {IsEmptyString, IsEmptyVar, IsEqualObj1, SafeClearTimeout} from '../../shared/defines/common.func';
import {BEAR_ANIM, GetRegion2Name, SearchParam} from '../../shared/defines/client.const';
import {GREETING, STAR_FILTER} from '../../shared/defines/protocol';
import {
  findMainJasoList,
  isMainJASO,
  JASO,
  MAIN_JASO,
  MAIN_JASO_LIST,
  REGION2,
  REGION2_DEFAULT,
  SUB_INDEX_MAP
} from '../../shared/defines/gukeo.const';

import {SearchHistory} from '../../shared/models/history.model';
import {ExampleCatTree, ExamplePostpositionTree} from '../../shared/models/example-tree.model';
import {LayoutMgrService} from '../../shared/services/layout-mgr/layout-mgr.service';
import {OptionService} from '../../shared/services/option/option.service';
import {LIST_HELP_FLAG, ListHelpService} from '../../shared/services/list-help/list-help.service';
import {BearService} from '../../shared/services/bear/bear.service';
import {GlobalEventService} from "../../shared/services/global-event/global-event.service";
import {ExampleService} from '../../shared/services/example/example.service';
import {
  NormalizeSearchKeyword,
  NormalizeSearchParamKeyword,
  SEARCH_KEYWORD_MAX_LENGTH
} from '../../shared/defines/search-keyword.func';


const AUTO_SEARCH_DELAY = 2000;     // 2초; 단어 검색 상자에 입력하고 나서 지연시간 동안 입력이 없으면 자동으로 검색한다.

const GREETING_ARROW_SHOW = 5000;    // 인사말 화살표 표시 시간
const GREETING_ARROW_HIDE = 8000;   // 인사말 화살표 숨김 시간
const EXAMPLE_CAT_LABEL_FULL_NAMES = new Set(['용언의 활용 등']);


type STAR_FILTER_ITEM = {
  value: STAR_FILTER;
  class: string|null;
  text: string|null;
};

const STAR_FILTER_ITEMS: Array<STAR_FILTER_ITEM> = [
  { value: STAR_FILTER.ALL,   class: null,          text: '모두보기' },
  { value: STAR_FILTER.TWO,   class: 'ic ic-star2', text: null },
  { value: STAR_FILTER.THREE, class: 'ic ic-star3', text: null },
];


@Component({
  selector: 'app-example-header',
  templateUrl: './example-header.component.html',
  styleUrls: ['./example-header.component.scss']
})
export class ExampleHeaderComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('fixed_header') fixedHeader: ElementRef;
  @ViewChild('jasoContainer') jasoContainer: ElementRef;
  @ViewChild('exampleCatMenuBox') exampleCatMenuBox: ElementRef;

  @Input() main: boolean;
  @Input() placeholder: string;
  @Input() showJasoIdx: boolean;
  @Input() curJasoIdx: JASO | null = null;
  @Input() secondJaso = true;  // 2차 색인으로 들어갈 지 여부.
  @Output() search: EventEmitter<SearchParam>;
  @Input() keyHistory: string;
  @Input() canGoHistory: boolean; // 이력 화면으로 이동할 수 있는지 여부
  @Input() keyword: string;
  @Input() greeting: GREETING;
  @Input() selectedRegion2: REGION2;
  @Input() showRegionSelect: boolean;

  @Output() goHistory: EventEmitter<any>;
  @Output() clickMapBtn: any;
  @Output() clickGreeting: EventEmitter<any>;

  protected jasoList: Array<JASO> = MAIN_JASO_LIST;
  protected mainJaso: MAIN_JASO | null = null; // 메인 자소 (2차 자소로 들어갈 때 저장해 놓는다. 복구를 위해서)

  public get height(): number {
    return this.fixedHeader.nativeElement.offsetHeight;
  }

  // 입력 창 관련
  public searchForm: FormGroup;
  public get ctlKeyword(): AbstractControl   { return this.searchForm.controls.keyword; }
  public readonly SearchKeywordMaxLength = SEARCH_KEYWORD_MAX_LENGTH;
  private lastParam: SearchParam = null;  // 같은 값 중복 호출 방지

  // 자소 색인
  // public JasoIdxData = JASO_IDX_DATA;

  private handleAutoSearch = null;

  // 별-필터 관련 정의
  public starFilterItems = STAR_FILTER_ITEMS;
  public showStarFilterMenu = false;      // star-filter 메뉴 열기
  public get curStarFilter(): STAR_FILTER { // 현재 선택된 별-필터의 값
    return this.option.wordLevel;
  }
  public set curStarFilter(value: STAR_FILTER) {
    this.option.wordLevel = value;
  }

  public history: SearchHistory;

  public get cssBear(): string {
    const bearAni = this.bearSvc.numAni;
    return bearAni === BEAR_ANIM.NORMAL ? '' : 'ani' + bearAni;
  }

  public queryParams: SearchParam;

  public treeList: Array<ExampleCatTree> = [];
  public selectedExampleCat: ExampleCatTree | null = null;
  public selectedPostposition: ExamplePostpositionTree | null = null;
  public showExamplePostpositions = false;
  public showExampleCatMenu = false;

  private tree$: Subscription | null = null;
  private queryParams$: Subscription | null = null;
  private globalClick$: Subscription | null = null;

  // showHandArrow를 Observable로 선언 (async 파이프를 통해 구독)
  public showHandArrow$: Observable<boolean>;

  private IsSetRegion2(): boolean {
    return !IsEmptyVar(this.selectedRegion2) && this.selectedRegion2 !== REGION2_DEFAULT;
  }

  constructor(
    private eRef: ElementRef,
    private fb: FormBuilder,
    private option: OptionService,
    private listHelpSvc: ListHelpService,
    private bearSvc: BearService,
    public lm: LayoutMgrService,
    private globalEventSvc: GlobalEventService,
    private route: ActivatedRoute,
    private exampleSvc: ExampleService,
  ) {
    // console.warn('# ExampleHeaderComponent::constructor');
    this.search = new EventEmitter<SearchParam>();
    this.goHistory = new EventEmitter<any>();
    this.clickMapBtn = new EventEmitter<any>();
    this.clickGreeting = new EventEmitter<void>();
  }

  ngOnInit(): void {
    // console.warn('# ExampleHeaderComponent::ngOnInit');

    this.searchForm = this.fb.group({
      keyword: [null],
    });

    this.lm.showIndex = this.showJasoIdx;
    this.ctlKeyword.setValue(NormalizeSearchKeyword(this.keyword));

    if (!IsEmptyString(this.keyHistory)) {
      this.history = new SearchHistory({
        name: this.keyHistory,
      });
    }

    this.changeJasoIdx(this.curJasoIdx);

    this.scrollToJaso(this.curJasoIdx);
    this.loadExampleTree();
    this.queryParams$ = this.route.queryParams.subscribe(params => {
      this.queryParams = NormalizeSearchParamKeyword({...params});
      this.applyExampleIndexFromQueryParams(this.queryParams);
    });
    this.globalClick$ = this.globalEventSvc.Click$.subscribe(event => {
      this.closeExampleCatMenuOnOutsideClick(event);
    });

    if (this.main) {
      // RxJS를 사용하여 비동기 객체로 손 모양 이미지 숨김/표시 사이클을 설정
      this.showHandArrow$ = concat(
        // 시작 시 즉시 false를 emit하여 이미지를 숨김
        timer(0).pipe(mapTo(false)),
        // GREETING_ARROW_HIDE 시간 후 true를 emit하여 이미지를 표시
        timer(GREETING_ARROW_HIDE).pipe(mapTo(true))
      ).pipe(
        // concat이 완료된 후 GREETING_ARROW_SHOW 시간 지연 후 반복
        repeatWhen(notifications => notifications.pipe(delay(GREETING_ARROW_SHOW)))
      );
      }
  }

  ngAfterViewInit(): void {
    // console.warn('# ExampleHeaderComponent::ngAfterViewInit');
  }

  ngOnDestroy(): void {
    // console.warn('# ExampleHeaderComponent::ngOnDestroy');
    SafeClearTimeout(this.handleAutoSearch);
    this.handleAutoSearch = null;
    this.bearSvc.typing = false;
    this.bearSvc.reset();
    if (this.tree$) {
      this.tree$.unsubscribe();
      this.tree$ = null;
    }
    if (this.queryParams$) {
      this.queryParams$.unsubscribe();
      this.queryParams$ = null;
    }
    if (this.globalClick$) {
      this.globalClick$.unsubscribe();
      this.globalClick$ = null;
    }
  }

  /****************************************************************************
   * 검색 이력
   ****************************************************************************/

  /**
   * 검색 이력을 클릭했을 때, 이벤트를 발생시키는 함수
   *
   * 주의:
   *    this.keyword 값은 직접 변경하지 않는다.
   *    그렇게 하면 OnChanges() 같은 변화 감지 구조에 문제가 생긴다.
   *    this.keyword를 수정하려면, 외부 이벤트 처리 함수를 통해서 변경해야 한다.
   *
   * @param keyword
   */
  onHistoryClick(keyword: string): void {
    // this.keyword = keyword;  // 직접 넣으면 안됨.
    keyword = NormalizeSearchKeyword(keyword);
    this.ctlKeyword.setValue(keyword);

    // lastParam 업데이트
    if (this.lastParam) {
      if (IsEmptyString(keyword)) {
        delete this.lastParam.keyword;
      } else {
        this.lastParam.keyword = keyword;
      }
    }

    this.EmitSearch(keyword ? {keyword} : {}, false);  // 검색이력 버튼은, 무조건 검색 이벤트를 발생해야 함.
  }

  onHistoryDel(index: number): void {
    this.history.removeFromIndex(index);
  }

  onClickGoHistory(): void {
    // 현재 사용하지 않음!
    // this.lastParam = null;
    // this.goHistory.emit();
  }

  /****************************************************************************
   * 예문 전용 색인 처리
   ****************************************************************************/
  private loadExampleTree(): void {
    const tree = this.exampleSvc.getTree();
    if (tree) {
      this.treeList = tree;
      this.applyExampleIndexFromQueryParams(this.queryParams);
      return;
    }

    this.tree$ = this.exampleSvc.GetTree().subscribe(list => {
      this.treeList = list;
      this.tree$ = null;
      this.applyExampleIndexFromQueryParams(this.queryParams);
    });
  }

  public getExampleCatLabel(cat: ExampleCatTree): string {
    const catName = (cat.name ?? '').replace(/\s+/g, ' ').trim();
    if (EXAMPLE_CAT_LABEL_FULL_NAMES.has(catName)) {
      return catName;
    }

    const names = catName.split(' ').filter(item => item.length > 0);
    if (names.length === 0) {
      return catName;
    }

    const label = names.slice(0, 2).join(' ');
    return names.length > 2 ? label + '...' : label;
  }

  public onClickExampleCat($event: MouseEvent, cat: ExampleCatTree): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    this.selectedExampleCat = cat;
    this.selectedPostposition = null;
    this.showExamplePostpositions = true;
    this.showExampleCatMenu = false;
    this.ctlKeyword.setValue(null);
    this.EmitSearch({example_cat: cat.id});
  }

  public onClickExampleCatMenu($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    this.showExampleCatMenu = !this.showExampleCatMenu;
  }

  public onClickExampleCatMenuClose($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();

    this.showExampleCatMenu = false;
  }

  private closeExampleCatMenuOnOutsideClick(event: MouseEvent|TouchEvent): void {
    if (!this.showExampleCatMenu) {
      return;
    }

    const menuBox = this.exampleCatMenuBox?.nativeElement;
    if (!menuBox) {
      this.showExampleCatMenu = false;
      return;
    }

    const target = event.target as Node | null;
    if (target && menuBox.contains(target)) {
      return;
    }

    this.showExampleCatMenu = false;
  }

  public onClickExampleCatAll($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    this.selectedExampleCat = null;
    this.selectedPostposition = null;
    this.showExamplePostpositions = false;
    this.showExampleCatMenu = false;
    this.ctlKeyword.setValue(null);
    this.EmitSearch({});
  }

  public onClickExampleIndexReturn($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    this.selectedExampleCat = null;
    this.selectedPostposition = null;
    this.showExamplePostpositions = false;
    this.showExampleCatMenu = false;
    this.ctlKeyword.setValue(null);
    this.EmitSearch({});
  }

  public onClickExamplePostposition($event: MouseEvent, postposition: ExamplePostpositionTree): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    this.selectedPostposition = postposition;
    this.ctlKeyword.setValue(null);
    this.EmitSearch({pp: postposition.id});
  }

  private applyExampleIndexFromQueryParams(params: SearchParam | null): void {
    if (!this.treeList || this.treeList.length === 0) {
      return;
    }

    if (!params || 'keyword' in params) {
      this.selectedExampleCat = null;
      this.selectedPostposition = null;
      this.showExamplePostpositions = false;
      this.showExampleCatMenu = false;
      return;
    }

    if ('pp' in params) {
      const ppId = Number(params.pp);
      for (const cat of this.treeList) {
        const pp = cat.postpositions.find(item => item.id === ppId);
        if (pp) {
          this.selectedExampleCat = cat;
          this.selectedPostposition = pp;
          this.showExamplePostpositions = true;
          this.showExampleCatMenu = false;
          return;
        }
      }
    }

    if ('example_cat' in params) {
      const catId = Number(params.example_cat);
      const cat = this.treeList.find(item => item.id === catId) ?? null;
      this.selectedExampleCat = cat;
      this.selectedPostposition = null;
      this.showExamplePostpositions = !!cat;
      this.showExampleCatMenu = false;
      return;
    }

    this.selectedExampleCat = null;
    this.selectedPostposition = null;
    this.showExamplePostpositions = false;
    this.showExampleCatMenu = false;
  }

  /****************************************************************************
   * 자소 색인 처리
   ****************************************************************************/

  ngOnChanges(changes: SimpleChanges) {
    // changes 객체는 변경된 @Input() 값들의 현재 및 이전 값을 포함합니다.

    // if (jasoChange) {
    //   console.warn('JasoIndexComponent::ngOnChanges', this.curJasoIdx, jasoChange.previousValue, jasoChange.currentValue);
    //   console.warn('curJasoIdx 이전 값:', jasoChange.previousValue);
    //   console.warn('curJasoIdx 현재 값:', jasoChange.currentValue);
    // }

    const jasoChange = changes['curJasoIdx'];
    if (jasoChange && jasoChange.previousValue !== jasoChange.currentValue) {
      if (jasoChange.previousValue !== undefined) {
        this.changeJasoIdx(jasoChange.currentValue, jasoChange.previousValue);
      }
      else {
        this.setJasoIdx(jasoChange.currentValue);
      }

      this.scrollToJaso(this.curJasoIdx);
    }

    const keywordChange = changes['keyword'];
    if (keywordChange && keywordChange.previousValue !== keywordChange.currentValue) {
      // searchForm이 초기화되었을 때만 setValue 호출
      if (this.searchForm) {
        console.warn('ExampleHeaderComponent:ngOnChanges()', keywordChange.currentValue, this.ctlKeyword);
        const keyword = NormalizeSearchKeyword(keywordChange.currentValue);
        this.ctlKeyword.setValue(keyword);

        // 외부에서 값을 리셋할 때, lastParam도 리셋한다.
        if (this.lastParam) {
          if (IsEmptyString(keyword)) {
            delete this.lastParam.keyword;
          }
          else {
            this.lastParam.keyword = keyword;
          }
        }
      }
    }
  }

  public onClickJasoIdx($event: MouseEvent, jaso: JASO | null): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.globalEventSvc.Click($event, true);

    this.changeJasoIdx(jaso, this.curJasoIdx, true);

    this.scrollToJaso(this.curJasoIdx);
  }


  /**
   * 자소 색인을 설정한다.
   * @param jaso
   * @private
   */
  private setJasoIdx(jaso: JASO | null): void {
    // console.warn('JasoIndexComponent::setJasoIdx', jaso);

    if (jaso && this.secondJaso) {
      if (isMainJASO(jaso)) {
        const subJasoList = SUB_INDEX_MAP.get(jaso);
        this.jasoList = subJasoList ?? MAIN_JASO_LIST;
        this.mainJaso = subJasoList ? jaso : null;  // 메인 자소라도 2차 색인이 없으면, null로 설정한다.
      } else {
        // SUB_INDEX_MAP 를 뒤져서 jaso에 들어있는 인덱스를 넣는다.
        [this.mainJaso, this.jasoList] = findMainJasoList(jaso) ?? [null, MAIN_JASO_LIST as string[]];
      }
    }
  }

  /**
   * 자소 색인을 변경한다.
   * @param jaso
   * @param oldJasoIdx - ngOnChanges()에서 넘어올 때는 이미 curJasoIdx가 변경되어 있다. 그래서 이전 값을 명시적으로 넘겨주도록 함.
   * @param emit - emit를 할지 여부. 컴포넌트 외부에서 주어진 값인 경우, 다시 밖으로 emit를 할 필요가 없다. (불필요하고, 꼬이게 만든다)
   */
  private changeJasoIdx(jaso: string | null, oldJaso: JASO | null = this.curJasoIdx, emit: boolean = false) {
    // console.warn('JasoIndexComponent::changeJasoIdx', jaso, oldJaso, this.curJasoIdx);

    if (oldJaso !== jaso)
    {
      this.curJasoIdx = jaso;
      this.ctlKeyword.setValue(null);
      let param: SearchParam = null;

      if (jaso === null) {
        this.resetJaso();

        param = this.IsSetRegion2() ? {region2: this.selectedRegion2} : {}; // for emit
      } else {
        this.setJasoIdx(jaso);

        param = {
          ...(this.IsSetRegion2() ? {region2: this.selectedRegion2} : {}),
          index: jaso,
        }; // for emit
      }

      if (emit && param) {
        this.search.emit(param);
        this.lastParam = {...param};
      }
    }
  }

  private resetJaso() {
    // console.warn('resetJaso()');

    this.curJasoIdx = null;
    this.mainJaso = null;
    this.jasoList = MAIN_JASO_LIST;
  }

  /**
   * 100ms 뒤에 자소 색인으로 스크롤한다.
   *
   * @param jaso - 선택된 자소 색인, null 인 경우에는 가장 왼쪽으로 스크롤 한다.
   * @private
   */
  private scrollToJaso(jaso: JASO | null = null): void {
    // console.warn('scrollToJaso()', this.curJasoIdx, this.jasoContainer);

    timer(100).subscribe(() => {
      // console.warn('scrollToJaso()', jaso, this.jasoContainer);

      if (jaso) {
        const elJaso = this.jasoContainer.nativeElement.querySelector('#' + jaso);
        // console.warn('scrollToJaso()', elJaso);
        if (elJaso) {
          elJaso.scrollIntoView({behavior: 'smooth'});
        }
      }
      else if (this.jasoContainer) {
        // 가장 왼쪽으로 스크롤
        this.jasoContainer.nativeElement.scrollLeft = 0;
      }
    });
  }

  /****************************************************************************
   * 검색 처리
   ****************************************************************************/

  /**
   * 검색 이벤트를 emit하는 함수
   *
   * @param param - 검색할 파라미터
   * @param checkSameValue - 이전 검색값과 비교 여부.
   * true면 이전과 같은 값일 때 emit하지 않고, false면 무조건 emit한다.
   */
  public EmitSearch(param: SearchParam, checkSameValue = true): void {
    param = NormalizeSearchParamKeyword(param);
    console.warn('ExampleHeaderComponent::EmitSearch()', param, this.lastParam);
    // 자동 검색 대기중이라면 지운다.
    SafeClearTimeout(this.handleAutoSearch);
    this.handleAutoSearch = null;
    this.bearSvc.typing = false;

    // 선택된 지역 정보가 있으면 반영한다.
    if (this.IsSetRegion2()) {
      // console.log('ExampleHeaderComponent::EmitSearch()', this.selectedRegion);
      param.region2 = this.selectedRegion2;
    }

    if (!checkSameValue || !(this.lastParam && IsEqualObj1(param, this.lastParam))) {
      console.log('ExampleHeaderComponent::EmitSearch()', param, this.lastParam);
      this.lastParam = {...param};  // 주의: 작업 도중에 param이 변경된다. 그래서 복사해서 보관해놓는다.
      this.search.emit(param);

      if ('keyword' in param) {
        this.resetJaso();
        this.selectedExampleCat = null;
        this.selectedPostposition = null;
        this.showExamplePostpositions = false;
        this.showExampleCatMenu = false;

        this.history.addItem(param.keyword);
        this.listHelpSvc.SetFlag(LIST_HELP_FLAG.SEARCH);
      }
    }
  }

  /**
   * 단어 검색 버튼을 눌렀을 때 검색 처리 함수
   * @param keyword - 검색할 단어
   */
  public onPushSearchBtn($event: MouseEvent, keyword: string): void {
    console.log('WordComponent::onPushSearchBtn()', $event, keyword);

    keyword = NormalizeSearchKeyword(keyword);
    this.ctlKeyword.setValue(keyword);

    if (!IsEmptyString(keyword)) {
      this.EmitSearch({keyword});
    }
  }


  /**
   * 검색 박스에 입력값이 들어오면 (값이 변하면) 호출된다.
   */
  public onInputSearchBox($event: Event): void {
    const tar: any = $event.target || $event.srcElement || $event.currentTarget;
    console.log(`WordComponent::onInputSearchBox() = "${tar.value}"`, $event);
    this.onSearchBoxValueChange(NormalizeSearchKeyword(tar.value));
  }

  public onPasteSearchBox($event: ClipboardEvent, elInput: HTMLInputElement): void {
    const text = $event.clipboardData?.getData('text');
    if (text === undefined) {
      return;
    }

    $event.preventDefault();
    const value = elInput.value ?? '';
    const start = elInput.selectionStart ?? value.length;
    const end = elInput.selectionEnd ?? start;
    const keyword = NormalizeSearchKeyword(value.slice(0, start) + text + value.slice(end));

    elInput.value = keyword;
    this.ctlKeyword.setValue(keyword);
    this.onSearchBoxValueChange(keyword);
  }

  private onSearchBoxValueChange(keyword: string): void {
    if (this.handleAutoSearch !== null) {
      clearTimeout(this.handleAutoSearch);
      this.handleAutoSearch = null;
    }

    if (!IsEmptyString(keyword)) {
      this.handleAutoSearch = setTimeout(() => {
        this.curJasoIdx = null;
        this.EmitSearch({keyword});
        this.bearSvc.typing = false;
      }, AUTO_SEARCH_DELAY);

      this.bearSvc.typing = true;
    }
    else {
      this.bearSvc.typing = false;
    }
  }


  /**
   * 검색 박스에 입력되는 키 값을 확인해서 Enter면 검색을 시도한다.
   */
  public onKeyUpSearchBox($event: KeyboardEvent, keyword: string): void {
    // console.log('WordComponent::onKeyUpSearchBox()', $event.key, $event.keyCode, $event);

    keyword = NormalizeSearchKeyword(keyword);
    if (($event.key === 'Enter' || $event.keyCode === 13) && !IsEmptyString(keyword)) {
      this.curJasoIdx = null;
      this.ctlKeyword.setValue(keyword);
      this.EmitSearch({ keyword });
    }
  }

  /**
   * 단어 검색창의 지우기 버튼 처리 함수. 검색창에 입력된 내용을 지운다.
   */
  public onPushClearBtn($event: MouseEvent, elInput: HTMLInputElement): void {
    console.log('WordComponent::onPushClearBtn()', $event, elInput);
    elInput.value = '';
    this.EmitSearch({});  // 검색을 지워도 외부에서 목록이 다시 로딩되어야 해서 추가.
  }


  /****************************************************************************
   * 검색창에 사투리 지역 반영.
   ****************************************************************************/
  public GetPlaceholder(): string {
    let placeholder = this.placeholder ? this.placeholder : '검색어를 입력 해 보시오.';

    /**
     * 지역이 선택되었을 때의 예외 처리
     */
    if (placeholder && this.IsSetRegion2()) {
      // console.log('GetPlaceholder()', placeholder, this.selectedRegion);
      placeholder = GetRegion2Name(this.selectedRegion2) + ' 지역의 우리말';
    }
    return placeholder;
  }


  public GetSelectRegionTxt(): string | null {
    return IsEmptyVar(this.selectedRegion2) ? null : this.selectedRegion2;
  }

  public onMapBtn(): void {
    this.clickMapBtn.emit();
  }

  /****************************************************************************
   * 인사말 클릭 시
   ****************************************************************************/
  onClickGreeting($event: MouseEvent) {
    // console.warn('ExampleHeaderComponent::onClickGreeting:', $event)
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    this.clickGreeting.emit();
  }

  /****************************************************************************
   * 별-필터 처리 코드
   ****************************************************************************/
  // public SetStarFilter( starFilter: STAR_FILTER ): void {
  //   this.curStarFilter = starFilter;
  //   this.showStarFilterMenu = false;
  //
  //   this.EmitSearch(null);
  // }
  //
  // public get curStarFilterItem(): STAR_FILTER_ITEM {
  //   const starFilter = STAR_FILTER_ITEMS.find(item => item.value === this.curStarFilter);
  //   if (!starFilter) {
  //     console.error('curStarFilgerItem: invalid curStarFilger value ', this.curStarFilter);
  //   }
  //   return starFilter;
  // }


}
